import { useReportPaging } from '@web/components/report-pagination.tsx';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { reportsApi } from '@web/api/endpoints.ts';
import { EmptyState } from '@web/components/ui.tsx';
import { BarListChart, CHART_SERIES, type BarListRow } from '@web/components/reports/charts.tsx';
import { formatMinutes } from '@web/i18n/format.ts';
import type { CapacityWeekRow } from '@shared/types/index.ts';
import { reportRetry, formatDay, ReportBoundary, SectionCard } from '../shared.tsx';

export function CapacitySection({ scope }: { scope?: 'all' }) {
  const { t } = useTranslation();
  const paging = useReportPaging({ scope });
  const pages = paging.pages;
  const query = useQuery({
    queryKey: ['reports', 'capacity', { scope, pages }, paging.identity],
    queryFn: () => reportsApi.capacity({ weeks: 4, scope, pages }),
    retry: reportRetry,
  });

  return (
    <ReportBoundary query={query} paging={paging}>
      {(report) => {
        if (
          report.weeks.every(
            (week, index) =>
              week.members.length === 0 &&
              week.unassignedMinutes === 0 &&
              (report.pagination?.[`week${index}`]?.page ?? 1) === 1,
          )
        ) {
          return <EmptyState title={t('reports.capacity.emptyTitle')}>{t('reports.capacity.emptyBody')}</EmptyState>;
        }
        return (
          <div className="space-y-6">
            <div className="flex justify-end">
              <a
                href={reportsApi.csvUrl('capacity', { weeks: 4, scope }).replace('format=csv', 'format=xlsx')}
                className="btn btn-secondary"
              >
                {t('reports.downloadXlsx')}
              </a>
              <a href={reportsApi.csvUrl('capacity', { weeks: 4, scope })} className="btn btn-secondary">
                {t('reports.downloadCsv')}
              </a>
            </div>
            {report.weeks.map((week, index) => (
              <CapacityWeekCard key={week.weekStart} week={week} collection={`week${index}`} />
            ))}
          </div>
        );
      }}
    </ReportBoundary>
  );
}

function CapacityWeekCard({ week, collection }: { week: CapacityWeekRow; collection: string }) {
  const { t } = useTranslation();
  const totalPercent =
    week.totalAvailableMinutes > 0 ? Math.round((week.totalBookedMinutes / week.totalAvailableMinutes) * 100) : null;
  const rows: BarListRow[] = week.members.map((member) => {
    const overbooked = (member.utilizationPercent ?? 0) > 100;
    return {
      label: member.name,
      value: member.utilizationPercent ?? 0,
      display:
        member.utilizationPercent === null
          ? formatMinutes(member.bookedMinutes)
          : `${member.utilizationPercent}% · ${formatMinutes(member.bookedMinutes)} / ${formatMinutes(member.availableMinutes)}`,
      // Over-capacity wears the danger tone AND says so in words — never the
      // color alone. No working hours at all reads as a note, not a bar.
      color: overbooked ? 'var(--color-danger)' : CHART_SERIES[0],
      ...(overbooked
        ? { note: t('reports.capacity.overbooked') }
        : member.utilizationPercent === null
          ? { note: t('reports.capacity.noHours') }
          : {}),
    };
  });

  return (
    <SectionCard
      collection={collection}
      title={t('reports.capacity.weekTitle', { from: formatDay(week.weekStart), to: formatDay(week.weekEnd) })}
      hint={
        totalPercent === null
          ? t('reports.capacity.weekHintNoHours', { booked: formatMinutes(week.totalBookedMinutes) })
          : t('reports.capacity.weekHint', {
              percent: totalPercent,
              booked: formatMinutes(week.totalBookedMinutes),
              available: formatMinutes(week.totalAvailableMinutes),
            })
      }
    >
      {rows.length === 0 ? (
        <p className="text-[color:var(--color-muted)]">{t('reports.capacity.weekEmpty')}</p>
      ) : (
        <BarListChart rows={rows} max={100} showTrack ariaLabel={t('reports.capacity.chartLabel')} />
      )}
      {week.unassignedMinutes > 0 ? (
        <p className="mt-2 text-sm">
          <span className="badge border-[color:var(--color-warning)] text-[color:var(--color-warning)]">
            {t('reports.capacity.unassignedBadge')}
          </span>{' '}
          <Link to="/schedule" className="text-[color:var(--color-brand)] hover:underline">
            {t('reports.capacity.unassigned', { time: formatMinutes(week.unassignedMinutes) })}
          </Link>
        </p>
      ) : null}
    </SectionCard>
  );
}
